import React from 'react';
import { Upload, Video, Camera, X } from 'lucide-react';

interface PhotoActionModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  onWebcam: () => void;
  onIPCamera: () => void;
  title: string;
  darkMode: boolean;
  hideIPCamera?: boolean;
}

const PhotoActionModal: React.FC<PhotoActionModalProps> = ({
  open,
  onClose,
  onUpload,
  onWebcam,
  onIPCamera,
  title,
  darkMode,
  hideIPCamera = false,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className={`
          relative w-full max-w-md mx-4 rounded-2xl shadow-2xl
          ${darkMode ? 'bg-gray-900' : 'bg-white'}
          animate-slideUp
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className={`
            absolute -top-3 -right-3 p-2 rounded-full shadow-lg
            ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-600'}
            transition-colors duration-200
          `}
          title="Fechar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className={`px-6 py-5 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {title}
          </h3>
          <p className={`text-sm text-center mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Escolha como adicionar a foto
          </p>
        </div>

        {/* Action buttons */}
        <div className="p-6 space-y-3">
          {/* Upload button */}
          <button
            onClick={() => {
              onUpload();
              onClose();
            }}
            className={`
              w-full flex items-center gap-4 p-4 rounded-xl
              ${darkMode
                ? 'bg-gray-800 hover:bg-gray-700 border-gray-700'
                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
              }
              border-2 transition-all duration-200 hover:scale-[1.02] hover:shadow-md
              group
            `}
          >
            <div className="flex-shrink-0 p-3 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
              <Upload className="w-6 h-6 text-blue-500" />
            </div>
            <div className="flex-1 text-left">
              <div className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Upload de Arquivo
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Selecionar do computador
              </div>
            </div>
          </button>

          {/* Webcam button */}
          <button
            onClick={() => {
              onWebcam();
              onClose();
            }}
            className={`
              w-full flex items-center gap-4 p-4 rounded-xl
              ${darkMode
                ? 'bg-gray-800 hover:bg-gray-700 border-gray-700'
                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
              }
              border-2 transition-all duration-200 hover:scale-[1.02] hover:shadow-md
              group
            `}
          >
            <div className="flex-shrink-0 p-3 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
              <Video className="w-6 h-6 text-green-500" />
            </div>
            <div className="flex-1 text-left">
              <div className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Capturar com Webcam
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Usar câmera do computador
              </div>
            </div>
          </button>

          {/* IP Camera button */}
          {!hideIPCamera && <button
            onClick={() => {
              onIPCamera();
              onClose();
            }}
            className={`
              w-full flex items-center gap-4 p-4 rounded-xl
              ${darkMode
                ? 'bg-gray-800 hover:bg-gray-700 border-gray-700'
                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
              }
              border-2 transition-all duration-200 hover:scale-[1.02] hover:shadow-md
              group
            `}
          >
            <div className="flex-shrink-0 p-3 rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
              <Camera className="w-6 h-6 text-amber-500" />
            </div>
            <div className="flex-1 text-left">
              <div className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Câmera IP
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Capturar de câmera IP
              </div>
            </div>
          </button>}
        </div>

        {/* Cancel button */}
        <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`
              w-full py-3 px-4 rounded-lg font-medium
              ${darkMode
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }
              transition-colors duration-200
            `}
          >
            Cancelar
          </button>
        </div>
      </div>

      <style>{`
        @keyframes photoModalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes photoModalSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fadeIn {
          animation: photoModalFadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: photoModalSlideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PhotoActionModal;
